import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  Smoking,
  SonicFacilityData,
} from "../../../common/classes/SonicFacility";
import { SonicUserData } from "../../../common/classes/SonicUser";
import { ImageSourceSetData } from "../../../common/classes/ImageSourceSet";
import Form from "../../../common/forms/Form";
import FormInput from "../../../common/forms/FormInput";
import ExpansionImage from "../../../common/ExpansionImage";

import styles from "../db-list/detail/DatabaseDetail.module.scss";

import { ValType } from "../../../utils/ValidationFunctions";
import {
  facilityTraitOptions,
  facilityTypeOptions,
} from "../../../utils/FacilityState";
import CheckInputArea from "../../../common/forms/CheckInputArea";
import { PriceStatus } from "../../../utils/PriceStatus";

type Props = {
  facility?: SonicFacilityData;
  users: SonicUserData[];
  imgItems: (ImageSourceSetData | File)[];
  traits: Array<string>;
  types: Array<string>;
  ownerName: string;
  setOwnerName: (value: string) => void;
  handleInputChange: (value: File | string | undefined, name: string) => void;
  deleteImgItem: (i: number, img: File | ImageSourceSetData) => void;
  handleTraits: (value: string) => void;
  handleTypes: (value: string) => void;
};

const FacilityForm: React.FC<Props> = ({
  facility,
  users,
  imgItems,
  traits,
  types,
  ownerName,
  setOwnerName,
  handleInputChange,
  deleteImgItem,
  handleTraits,
  handleTypes,
}) => {
  const [imgOpen, setImgOpen] = useState<boolean>(false);
  const [imgIndex, setImgIndex] = useState<number | null>(null);
  const [filtered, setFiltered] = useState<SonicUserData[]>([]);

  const search = (value: string) => {
    if (!value) return setFiltered(users);
    const filteredList = users.filter(
      (item) => item.meta.name && item.meta.name.includes(value)
    );
    setFiltered(filteredList);
  };

  return (
    <Form handler={() => {}}>
      <div
        className={`${styles["f-databaseDetail__form"]} ${styles["error-space"]}`}
      >
        <FormInput
          type="text"
          label="施設名"
          label_class={`${styles["f-databaseDetail__form-title"]} ${styles["required-mark"]}`}
          name="sonic_name"
          style_class={styles["f-databaseDetail__form-input"]}
          handler={handleInputChange}
          initial_value={facility?.name ?? ""}
          val_type={ValType.NotNull}
        />
      </div>
      <div className={styles["f-databaseDetail__form"]}>
        <FormInput
          type="text"
          label="かな"
          label_class={styles["f-databaseDetail__form-title"]}
          name="sonic_kana"
          style_class={styles["f-databaseDetail__form-input"]}
          handler={handleInputChange}
          initial_value={facility?.kana ?? ""}
        />
      </div>
      <div className={styles["f-databaseDetail__form"]}>
        <FormInput
          type="prefecture"
          label="都道府県"
          label_class={styles["f-databaseDetail__form-title"]}
          placeholder="選択してください"
          name="sonic_prefecture"
          select_class={styles["f-databaseDetail__form-input"]}
          holder_class={styles["f-databaseDetail__form-input"]}
          handler={handleInputChange}
          initial_value={facility?.prefecture ?? ""}
        />
      </div>
      <div className={styles["f-databaseDetail__form"]}>
        <FormInput
          type="text"
          label="住所"
          label_class={styles["f-databaseDetail__form-title"]}
          name="sonic_address"
          style_class={styles["f-databaseDetail__form-input"]}
          handler={handleInputChange}
          initial_value={facility?.address ?? ""}
        />
      </div>
      <div className={styles["f-databaseDetail__form"]}>
        <FormInput
          type="text"
          label="map"
          label_class={styles["f-databaseDetail__form-title"]}
          name="sonic_map"
          style_class={styles["f-databaseDetail__form-input"]}
          handler={handleInputChange}
          initial_value={facility?.map ?? ""}
        />
      </div>
      <div className={styles["f-databaseDetail__form"]}>
        <FormInput
          type="textarea"
          label="アクセス"
          label_class={styles["f-databaseDetail__form-title"]}
          name="sonic_access"
          style_class={`${styles["f-databaseDetail__form-input"]} ${styles["resizable"]}`}
          handler={handleInputChange}
          initial_value={facility?.access ?? ""}
          rows={5}
        />
      </div>
      <div className={styles["f-databaseDetail__form"]}>
        <FormInput
          type="textarea"
          label="営業時間"
          label_class={styles["f-databaseDetail__form-title"]}
          name="sonic_business_days"
          style_class={`${styles["f-databaseDetail__form-input"]} ${styles["resizable"]}`}
          handler={handleInputChange}
          initial_value={facility?.businessDays ?? ""}
        />
      </div>
      <div className={styles["f-databaseDetail__form"]}>
        <FormInput
          type="text"
          label="電話番号"
          label_class={styles["f-databaseDetail__form-title"]}
          name="sonic_tel"
          style_class={styles["f-databaseDetail__form-input"]}
          handler={handleInputChange}
          initial_value={facility?.tel ?? ""}
        />
      </div>
      <div className={styles["f-databaseDetail__form"]}>
        <FormInput
          type="textarea"
          label="料金"
          label_class={styles["f-databaseDetail__form-title"]}
          name="sonic_price"
          style_class={`${styles["f-databaseDetail__form-input"]} ${styles["resizable"]}`}
          handler={handleInputChange}
          initial_value={facility?.price ?? ""}
        />
      </div>
      <div className={styles["f-databaseDetail__form"]}>
        <FormInput
          type="textarea"
          label="座席数"
          label_class={styles["f-databaseDetail__form-title"]}
          name="sonic_seats"
          style_class={`${styles["f-databaseDetail__form-input"]} ${styles["resizable"]}`}
          handler={handleInputChange}
          initial_value={facility?.seats ?? ""}
        />
      </div>
      <div className={styles["f-databaseDetail__form"]}>
        <FormInput
          type="textarea"
          label="設備 / スペック"
          label_class={styles["f-databaseDetail__form-title"]}
          name="sonic_machines"
          style_class={`${styles["f-databaseDetail__form-input"]} ${styles["resizable"]}`}
          handler={handleInputChange}
          initial_value={facility?.machines ?? ""}
        />
      </div>
      <div className={styles["f-databaseDetail__form"]}>
        <FormInput
          type="textarea"
          label="施設紹介"
          label_class={styles["f-databaseDetail__form-title"]}
          name="sonic_introduction"
          style_class={`${styles["f-databaseDetail__form-input"]} ${styles["resizable"]}`}
          handler={handleInputChange}
          initial_value={facility?.introduction ?? ""}
        />
      </div>
      <div className={styles["f-databaseDetail__form"]}>
        <FormInput
          type="radio"
          label="喫煙設定"
          label_class={styles["f-databaseDetail__form-title"]}
          name="sonic_smoking"
          ul_class={styles["f-databaseDetail__form-radio"]}
          style_class={styles["f-databaseDetail__form-radio__input"]}
          handler={handleInputChange}
          initial_value={facility?.smoking ?? Smoking.None}
          options={[
            [Smoking.None, "設定なし"],
            [Smoking.NotAllow, "禁煙"],
            [Smoking.Separate, "分煙"],
            [Smoking.Allow, "喫煙可"],
          ]}
        />
      </div>
      <div className={styles["f-databaseDetail__form"]}>
        <FormInput
          type="radio"
          label="イベント開催"
          label_class={styles["f-databaseDetail__form-title"]}
          name="sonic_host_events"
          ul_class={styles["f-databaseDetail__form-radio"]}
          style_class={styles["f-databaseDetail__form-radio__input"]}
          handler={handleInputChange}
          initial_value={facility?.hostEvents ?? "event_none"}
          options={[
            ["event_none", "設定なし"],
            ["event_true", "OK"],
            ["event_false", "NG"],
          ]}
        />
      </div>
      <div className={styles["f-databaseDetail__form"]}>
        <FormInput
          type="radio"
          label="フード提供"
          label_class={styles["f-databaseDetail__form-title"]}
          name="sonic_food_service"
          ul_class={styles["f-databaseDetail__form-radio"]}
          style_class={styles["f-databaseDetail__form-radio__input"]}
          handler={handleInputChange}
          initial_value={facility?.foodService ?? "food_none"}
          options={[
            ["food_none", "設定なし"],
            ["food_true", "あり"],
            ["food_false", "なし"],
          ]}
        />
      </div>
      <div className={styles["f-databaseDetail__form"]}>
        <FormInput
          type="radio"
          label="予約利用可"
          label_class={styles["f-databaseDetail__form-title"]}
          name="sonic_reservation"
          ul_class={styles["f-databaseDetail__form-radio"]}
          style_class={styles["f-databaseDetail__form-radio__input"]}
          handler={handleInputChange}
          initial_value={facility?.reservation ?? "reservation_none"}
          options={[
            ["reservation_none", "設定なし"],
            ["reservation_true", "OK"],
            ["reservation_false", "NG"],
          ]}
        />
      </div>
      <div className={styles["f-databaseDetail__form"]}>
        <div className={styles["f-databaseDetail__form-title"]}>施設タグ</div>
        <CheckInputArea
          name="sonic_traits"
          checkOptions={facilityTraitOptions}
          baseColor="black"
          checked={traits}
          handleChange={handleTraits}
        />
      </div>
      <div className={styles["f-databaseDetail__form"]}>
        <div className={styles["f-databaseDetail__form-title"]}>施設タイプ</div>
        <CheckInputArea
          name="sonic_facility_types"
          checkOptions={facilityTypeOptions}
          baseColor="black"
          checked={types}
          handleChange={handleTypes}
        />
      </div>
      <div className={styles["f-databaseDetail__form"]}>
        <FormInput
          type="select"
          label="料金ステータス"
          label_class={styles["f-databaseDetail__form-title"]}
          name="sonic_price_status"
          select_class={styles["f-databaseDetail__form-input"]}
          holder_class={styles["f-databaseDetail__form-input"]}
          input_wrap_class={styles["f-databaseDetail__form-chevronDown-select"]}
          handler={handleInputChange}
          initial_value={facility?.price_status ?? ""}
          options={[
            ["", ""],
            [PriceStatus.Reasonable, "リーズナブル"],
            [PriceStatus.Normal, "ノーマル"],
            [PriceStatus.HighEnd, "ハイエンド"],
          ]}
        />
      </div>
      <div className={styles["f-databaseDetail__form"]}>
        <FormInput
          type="text"
          label="収容人数(説明)"
          label_class={styles["f-databaseDetail__form-title"]}
          name="sonic_seating_capacity_text"
          style_class={styles["f-databaseDetail__form-input"]}
          handler={handleInputChange}
          initial_value={facility?.seating_capacity_text ?? ""}
        />
      </div>
      <div
        className={`${styles["f-databaseDetail__form"]} ${styles["error-space"]}`}
      >
        <FormInput
          type="positive_int"
          label="最大収容人数(数値)"
          label_class={styles["f-databaseDetail__form-title"]}
          name="sonic_seating_capacity_max"
          style_class={styles["f-databaseDetail__form-input"]}
          handler={handleInputChange}
          initial_value={facility?.seating_capacity_max ?? ""}
          val_type={ValType.PositiveInt}
        />
      </div>
      <div className={styles["f-databaseDetail__form"]}>
        <FormInput
          type="text"
          label="SELeCTレビュー"
          label_class={styles["f-databaseDetail__form-title"]}
          name="sonic_select_review"
          style_class={styles["f-databaseDetail__form-input"]}
          handler={handleInputChange}
          initial_value={facility?.select_review ?? ""}
        />
      </div>
      <div
        className={`${styles["f-databaseDetail__form"]} ${styles["error-space"]}`}
      >
        <FormInput
          type="text"
          label="公式サイト"
          label_class={styles["f-databaseDetail__form-title"]}
          name="sonic_official_site"
          style_class={styles["f-databaseDetail__form-input"]}
          handler={handleInputChange}
          initial_value={facility?.officialSite ?? ""}
          val_type={ValType.URL}
        />
      </div>
      <div
        className={`${styles["f-databaseDetail__form"]} ${styles["error-space"]}`}
      >
        <FormInput
          type="text"
          label="X (旧Twitter)"
          label_class={styles["f-databaseDetail__form-title"]}
          name="sonic_twitter"
          style_class={styles["f-databaseDetail__form-input"]}
          handler={handleInputChange}
          initial_value={facility?.sns?.twitter ?? ""}
          val_type={ValType.Twitter}
        />
      </div>
      <div
        className={`${styles["f-databaseDetail__form"]} ${styles["error-space"]}`}
      >
        <FormInput
          type="text"
          label="instagaram"
          label_class={styles["f-databaseDetail__form-title"]}
          name="sonic_instagram"
          style_class={styles["f-databaseDetail__form-input"]}
          handler={handleInputChange}
          initial_value={facility?.sns?.instagram ?? ""}
          val_type={ValType.Instagram}
        />
      </div>
      <div
        className={`${styles["f-databaseDetail__form"]} ${styles["error-space"]}`}
      >
        <FormInput
          type="text"
          label="facebook"
          label_class={styles["f-databaseDetail__form-title"]}
          name="sonic_facebook"
          style_class={styles["f-databaseDetail__form-input"]}
          handler={handleInputChange}
          initial_value={facility?.sns?.facebook ?? ""}
          val_type={ValType.Facebook}
        />
      </div>
      <div
        className={`${styles["f-databaseDetail__form"]} ${styles["error-space"]}`}
      >
        <FormInput
          type="text"
          label="youtube"
          label_class={styles["f-databaseDetail__form-title"]}
          name="sonic_youtube"
          style_class={styles["f-databaseDetail__form-input"]}
          handler={handleInputChange}
          initial_value={
            facility?.sns?.youtube
              ? facility.sns.youtube
              : "https://youtube.com/channel/"
          }
          val_type={ValType.Youtube}
        />
      </div>
      <div
        className={`${styles["f-databaseDetail__form"]} ${styles["error-space"]}`}
      >
        <span className={styles["f-databaseDetail__form-title"]}>
          施設画像 最大5枚
        </span>
        <div className={styles["f-databaseDetail__form-imgList"]}>
          {imgItems && imgItems.length ? (
            <div>
              {imgItems.map((img, i) => (
                <div key={i}>
                  <div
                    className={`${styles["imageBox"]} ${styles["clickable"]}`}
                    onClick={() => {
                      setImgOpen(true);
                      setImgIndex(i);
                    }}
                  >
                    <img
                      src={
                        img instanceof File
                          ? URL.createObjectURL(img)
                          : img.medium?.url
                      }
                    />
                  </div>
                  <button
                    className={`${styles["f-databaseDetail__form-fileBtn"]} ${styles["delete"]}`}
                    onClick={() => deleteImgItem(i, img)}
                  >
                    <FontAwesomeIcon icon={["fal", "trash-alt"]} />
                    削除
                  </button>
                </div>
              ))}
            </div>
          ) : null}
          {imgItems.length < 5 ? (
            <button
              type="button"
              className={`${styles["f-databaseDetail__form-fileBtn"]} ${styles["add"]}`}
            >
              <FontAwesomeIcon icon={["fal", "plus-circle"]} />
              <FormInput
                type="file"
                label="追加する"
                id="imgs"
                name="sonic_imgs"
                handler={handleInputChange}
                val_type={ValType.File}
              />
            </button>
          ) : null}
        </div>
      </div>
      <div className={styles["f-databaseDetail__form"]}>
        <label className={styles["f-databaseDetail__form-title"]}>
          会員情報連携
        </label>
        <input
          type="text"
          list="owners"
          onChange={(e) => {
            search(e.target.value);
            setOwnerName(e.target.value);
          }}
          value={ownerName}
          className={styles["f-databaseDetail__form-input"]}
        />
        <datalist id="owners">
          {filtered.map((item) => (
            <option key={item.id} value={item.meta.name}>
              {item.meta.name}
            </option>
          ))}
        </datalist>
      </div>
      {imgOpen && imgIndex !== null && imgItems?.length && (
        <ExpansionImage
          img={imgItems[imgIndex]}
          onClose={() => {
            setImgOpen(false);
            setImgIndex(null);
          }}
        />
      )}
    </Form>
  );
};

export default FacilityForm;
